import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, CheckCircle2, Star, Shield,
  BookOpen, GraduationCap, Mic, Camera, Award, Sparkles, Users,
} from '@/components/DeskeyIcons';

const G = '#34A853';
const G_DARK = '#1E7E34';

const Logo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853" />
  </svg>
);

const APPS = {
  teacher: {
    name: 'Deskey Teacher',
    audience: 'For Teachers',
    tagline: 'Pair your AI recorder, edit lessons, and publish on the go — everything you need to run a classroom, in your pocket.',
    icon: BookOpen,
    apkUrl: '/downloads/deskey-teacher.apk',
    version: '1.0.0',
    features: [
      { icon: Mic, title: 'AI recorder pairing', text: 'Pair with the Deskey AI recording device over Bluetooth' },
      { icon: Camera, title: 'Blackboard capture', text: 'Snap blackboard photos for instant AI extraction' },
      { icon: BookOpen, title: 'Edit & publish', text: 'Edit and publish lessons straight from your phone' },
      { icon: Shield, title: 'Secure sign-in', text: 'Sign in securely with your existing Deskey account' },
    ],
  },
  student: {
    name: 'Deskey Student',
    audience: 'For Students',
    tagline: 'Your desk, assignments, badges, and AI tutor — always in your pocket, wherever you learn.',
    icon: GraduationCap,
    apkUrl: '/downloads/deskey-student.apk',
    version: '1.0.0',
    features: [
      { icon: GraduationCap, title: 'Your desk, everywhere', text: 'Access your desk and every published lesson' },
      { icon: Award, title: 'Badges & progress', text: 'Track badges and progress as you learn' },
      { icon: Sparkles, title: 'AI tutor', text: 'Chat with your AI tutor anytime, anywhere' },
      { icon: Users, title: 'Stay in sync', text: 'Stay in sync with your class and assignments' },
    ],
  },
};

export default function MobileAppDownloadPage() {
  const { role } = useParams();
  const nav = useNavigate();
  const app = APPS[role] || APPS.student;
  const AppIcon = app.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={28} />
            <span className="text-base font-bold text-gray-900">Deskey</span>
          </div>
          <button
            type="button"
            onClick={() => nav('/')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-emerald-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Deskey
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: '#E8F5EC', color: G_DARK }}
          >
            <AppIcon className="w-3.5 h-3.5" /> {app.audience}
          </span>

          <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mt-5 leading-tight">{app.name}</h1>
          <p className="text-lg text-gray-500 mt-4 leading-relaxed max-w-lg">{app.tagline}</p>

          <div className="flex items-center gap-1 mt-5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-amber-400" style={{ fill: 'currentColor' }} />
            ))}
            <span className="text-sm text-gray-400 ml-2">Built for classrooms</span>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-8">
            <a
              href={app.apkUrl}
              download
              data-testid="download-apk-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl font-semibold text-white text-base hover:opacity-95 transition-opacity shadow-lg shadow-emerald-900/15"
              style={{ background: G }}
            >
              <Download className="w-5 h-5" /> Download for Android
            </a>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              Signed &amp; verified by Deskey
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Android APK &middot; v{app.version} &middot; You may need to allow &ldquo;install from unknown sources&rdquo;
          </p>
        </div>

        {/* Phone mockup */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative w-64">
            <div className="relative rounded-[2.75rem] border-[10px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
              <div
                className="aspect-[9/19] flex flex-col items-center justify-center px-6 text-center"
                style={{ background: `linear-gradient(165deg, ${G} 0%, ${G_DARK} 100%)` }}
              >
                <div className="w-16 h-16 rounded-2xl bg-white/95 flex items-center justify-center shadow-md">
                  <AppIcon className="w-8 h-8" style={{ color: G }} />
                </div>
                <p className="text-white font-bold text-lg mt-4">{app.name}</p>
                <div className="w-full mt-8 space-y-2.5">
                  {[100, 80, 90].map((w, i) => (
                    <div key={i} className="h-3 rounded-full bg-white/20" style={{ width: `${w}%`, marginInline: 'auto' }} />
                  ))}
                </div>
              </div>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10" />
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-emerald-50/50 border-y border-emerald-100/60">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide text-center mb-2">What you get</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 text-center mb-10">Everything in one app</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {app.features.map(({ icon: FeatIcon, title, text }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-emerald-100 p-6 hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <FeatIcon className="w-5 h-5" style={{ color: G }} />
                </div>
                <p className="text-sm font-semibold text-gray-900 mt-4">{title}</p>
                <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Deskey. All rights reserved.
      </div>
    </div>
  );
}
